import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip,
  BarChart, Bar, PieChart, Pie, Cell,
} from "recharts";
import { Users, TrendingUp, PiggyBank, Target, Sparkles, Loader2, RefreshCw, ChevronsUpDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { REPORTS } from "@/constants/testIds";
import ReactMarkdown from "@/components/common/Markdown";
import DateRangeControl, { dateParamsFrom } from "@/components/dashboard/DateRangeControl";
import DailySummaryCard from "@/components/dashboard/DailySummaryCard";
import DailySummaryByTeamCard from "@/components/dashboard/DailySummaryByTeamCard";
import LoadingOverlay from "@/components/common/LoadingOverlay";
import { useSortableTable, SortableTh } from "@/lib/useSortableTable";

const CHART_COLORS = ["#0A84FF", "#5B8C5A", "#C07D5E", "#8b5cf6", "#14b8a6", "#ef4444"];

/** "Data as of X min ago" banner + Rebuild button. Reads from the main
 * dashboard's pre-aggregate (main_daily_agg) refresh timestamp. */
function MainFreshnessBanner() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/analytics/refresh-status")
      .then((r) => setStatus(r.data))
      .catch(() => {});
  }, []);

  const last = status?.last_rebuilt_at ? new Date(status.last_rebuilt_at) : null;
  const relative = (() => {
    if (!last) return null;
    const s = Math.max(0, Math.round((Date.now() - last.getTime()) / 1000));
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.round(s / 60)} min ago`;
    if (s < 86400) return `${Math.round(s / 3600)} hr ago`;
    return `${Math.round(s / 86400)}d ago`;
  })();
  const isStale = last && (Date.now() - last.getTime()) > 15 * 60 * 1000;

  const rebuildNow = async () => {
    if (!window.confirm("Rebuild the entire main-dashboard cache?\n\nTakes a few seconds and won't disrupt live queries.")) return;
    setBusy(true);
    try {
      await api.post("/admin/rebuild-main-rollup", {});
      window.location.reload();
    } finally { setBusy(false); }
  };

  return (
    <div
      className={`glass-card px-4 py-2 flex items-center justify-between gap-3 ${
        isStale ? "border border-amber-300 bg-amber-50/70" : ""
      }`}
      data-testid="main-freshness-banner"
    >
      <div className="text-xs text-slate-600 flex items-center gap-2">
        <span className={`inline-block w-2 h-2 rounded-full ${isStale ? "bg-amber-500" : "bg-emerald-500"}`}></span>
        {last ? (
          <>
            Data as of <b className="text-slate-900">{relative}</b>
            <span className="text-slate-400 ml-1">({last.toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })})</span>
            <span className="text-slate-400 ml-2 hidden sm:inline">· auto-refresh every 10 min</span>
          </>
        ) : (
          <>Pre-aggregated dashboard cache not built yet — first upload will populate it.</>
        )}
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 text-xs"
        onClick={rebuildNow}
        disabled={busy}
        data-testid="main-rebuild-btn"
      >
        {busy ? <><Loader2 className="w-3 h-3 animate-spin mr-1" /> Rebuilding…</> : <><RefreshCw className="w-3 h-3 mr-1" /> Rebuild now</>}
      </Button>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, sub }) {
  return (
    <div className="glass-card p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500 font-medium">{label}</div>
          <div className="kpi-num text-3xl mt-1 text-slate-900">{value}</div>
          {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
        </div>
        <div className="w-10 h-10 rounded-xl bg-white/70 flex items-center justify-center">
          <Icon className="w-5 h-5 text-slate-700" />
        </div>
      </div>
    </div>
  );
}

// Small "📸 snapshot" pill placed next to the section title when the
// currently-shown data was served from the closed-month cache. Encourages
// operators to trust the frozen number and reminds them that any late
// re-enrichment will auto-invalidate the cache.
function CachedBadge({ at }) {
  if (!at) return null;
  const when = new Date(at).toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-[10px] text-slate-600"
          title={`Frozen snapshot from ${at}. Re-enrichment on this month would invalidate the snapshot and recompute.`}
          data-testid="cached-badge">
      📸 snapshot · {when}
    </span>
  );
}

// Compact KPI tile used inside the Cashflow panel — half the height,
// same information density.
function MiniKpi({ label, value, sub }) {
  return (
    <div className="bg-white/70 border border-sky-100 rounded-md px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">{label}</div>
      <div className="text-xl font-semibold text-slate-900 tabular-nums mt-0.5">{value}</div>
      {sub && <div className="text-[10px] text-slate-500">{sub}</div>}
    </div>
  );
}

/** MySQL-live "Deposits split" panel — replaces the confusing
 * lifetime "Deposits after" number with two clean windows:
 *   • In-month     — deposits landing IN the picked month
 *   • After-month  — deposits landing AFTER month-end, through today
 *
 * Users included: every ops_call_row with credit_status in
 * [yes, sms, wn, invalid, unsure] — the same "reactivated" universe
 * the main KPI cards use.
 *
 * Because this hits MySQL live through the SSH tunnel, it's a
 * click-to-load panel (10-30s per month) rather than an auto-fetch.
 */
function DepositsSplitPanel({ month, teamId, brandId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [startedAt, setStartedAt] = useState(null);

  const run = async (forceRefresh = false) => {
    setLoading(true);
    setErr("");
    setStartedAt(Date.now());
    try {
      const params = { month };
      if (teamId && teamId !== "all") params.team_id = teamId;
      if (brandId) params.brand_id = brandId;
      if (forceRefresh) params.force_refresh = 1;
      const r = await api.get("/admin/diagnose/deposits-split", { params });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  // Auto-clear cached result when the selected month/team changes so
  // the numbers on screen never lag behind the header controls.
  useEffect(() => { setData(null); setErr(""); }, [month, teamId, brandId]);

  return (
    <div className="glass-card p-4 border-l-4 border-indigo-400 bg-indigo-50/30"
         data-testid="deposits-split-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="font-outfit font-semibold text-slate-900 flex items-center gap-2">
            💰 Deposits split · live MySQL
            {month && <span className="text-xs font-normal text-slate-500">— {month}</span>}
            {data?._cached && (
              <span className="text-[10px] px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full"
                    title="Served from cache — click Refresh to re-query MySQL">
                cached
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            For every user credited=yes/sms/etc in {month || "the picked month"}, splits their MySQL PROCESSED deposits into
            <b> in-month</b> vs <b>after-month</b> (through today). Cached forever for closed months, 5-min TTL for the current month.
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => run(false)}
            disabled={loading || !month}
            size="sm"
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="deposits-split-load-btn"
          >
            {loading
              ? <><Loader2 className="w-3 h-3 animate-spin mr-1" /> Querying… ({Math.round((Date.now() - (startedAt || Date.now())) / 1000)}s)</>
              : data ? "Reload" : "Load"}
          </Button>
          {data?._cached && (
            <Button
              onClick={() => run(true)}
              disabled={loading}
              size="sm"
              variant="outline"
              data-testid="deposits-split-refresh-btn"
            >
              <RefreshCw className="w-3 h-3 mr-1" /> Force refresh
            </Button>
          )}
        </div>
      </div>

      {err && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1.5"
             data-testid="deposits-split-err">
          {err}
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <div className="bg-white/70 border border-indigo-100 rounded-md px-3 py-2">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Converted users</div>
              <div className="text-xl font-semibold text-slate-900 tabular-nums mt-0.5">{fmt(data.converted_users)}</div>
              <div className="text-[10px] text-slate-500">{data.per_brand?.length || 0} brands</div>
            </div>
            <div className="bg-white/70 border border-indigo-100 rounded-md px-3 py-2">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">In {data.month}</div>
              <div className="text-xl font-semibold text-slate-900 tabular-nums mt-0.5">{fmtMoney(data.in_month)}</div>
              <div className="text-[10px] text-slate-500">{fmt(data.in_month_txns)} txns</div>
            </div>
            <div className="bg-white/70 border border-indigo-100 rounded-md px-3 py-2">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">After {data.month}</div>
              <div className="text-xl font-semibold text-slate-900 tabular-nums mt-0.5">{fmtMoney(data.after_month)}</div>
              <div className="text-[10px] text-slate-500">{fmt(data.after_month_txns)} txns · through today</div>
            </div>
            <div className="bg-white/70 border border-indigo-100 rounded-md px-3 py-2">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Total (in + after)</div>
              <div className="text-xl font-semibold text-slate-900 tabular-nums mt-0.5">
                {fmtMoney((data.in_month || 0) + (data.after_month || 0))}
              </div>
              <div className="text-[10px] text-slate-500">{fmt((data.in_month_txns || 0) + (data.after_month_txns || 0))} txns</div>
            </div>
          </div>

          {data.per_brand?.length > 1 && (
            <div className="overflow-auto max-h-56 border border-indigo-100 rounded"
                 data-testid="deposits-split-per-brand">
              <table className="text-[11px] w-full">
                <thead className="bg-indigo-50 sticky top-0 text-left">
                  <tr>
                    <th className="px-2 py-1">Brand</th>
                    <th className="px-2 py-1 text-right">Users</th>
                    <th className="px-2 py-1 text-right">In month ₹</th>
                    <th className="px-2 py-1 text-right">After month ₹</th>
                    <th className="px-2 py-1 text-right">Total ₹</th>
                  </tr>
                </thead>
                <tbody>
                  {data.per_brand.map((b) => (
                    <tr key={b.brand_id} className="border-t border-indigo-50">
                      <td className="px-2 py-1 font-medium">#{b.brand_id}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{fmt(b.converted_users)}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{fmtMoney(b.in_month_amount)}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{fmtMoney(b.after_month_amount)}</td>
                      <td className="px-2 py-1 text-right tabular-nums font-semibold">
                        {fmtMoney((b.in_month_amount || 0) + (b.after_month_amount || 0))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="text-[10px] text-slate-500 mt-2">
            Note: numbers are mixed-currency when brands use different currencies. Filter to one brand for a currency-clean total.
          </div>
        </>
      )}

      {!data && !err && !loading && (
        <div className="text-xs text-slate-500">
          Click <b>Load</b> to run the MySQL check. Takes 10-60s depending on user volume.
        </div>
      )}
    </div>
  );
}

/** Dual-value KPI card comparing the month-close snapshot vs. today's
 * live recompute. Useful for spotting "how much did July grow in
 * lifetime deposits since we froze it?" — late deposits landing in
 * August get re-attributed to the original July call and only become
 * visible on the LIVE side, so the delta = re-enrichment growth. */
function FrozenVsGrowthCard({ month, teamId, campaignIds }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Compare by joined string to avoid unnecessary refetches whenever
  // the parent re-renders and hands us a new array reference.
  const campaignIdsKey = (campaignIds || []).join(",");

  useEffect(() => {
    if (!month) return;
    setLoading(true);
    setErr("");
    const params = { month };
    if (teamId && teamId !== "all") params.team_id = teamId;
    if (campaignIdsKey) params.campaign_ids = campaignIdsKey;
    api.get("/analytics/frozen-vs-growth", { params })
      .then((r) => setData(r.data))
      .catch((e) => {
        setData(null);
        setErr(e?.response?.data?.detail || "Failed to load frozen-vs-growth.");
      })
      .finally(() => setLoading(false));
  }, [month, teamId, campaignIdsKey]);

  if (!month) return null;

  const rows = [
    { key: "reactivated", label: "Reactivated users", money: false },
    { key: "ftd_on_day", label: "FTD on-day", money: true, subKey: "ftd_on_day_count", subFmt: (n) => `${fmt(n)} FTDs` },
    { key: "sum_on", label: "Deposits on-day", money: true },
    { key: "sum_after", label: "Deposits after (lifetime)", money: true, growthEmphasis: true },
    { key: "ltv", label: "Approx LTV", money: true },
  ];

  return (
    <div className="glass-card p-4 border-l-4 border-emerald-400 bg-emerald-50/30"
         data-testid="frozen-vs-growth-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="font-outfit font-semibold text-slate-900 flex items-center gap-2">
            📸 Month-close snapshot vs live growth
            <span className="text-xs font-normal text-slate-500">— {month}</span>
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Left column = value frozen at month-end. Right column = today. Growth = late deposits attributed backwards via re-enrichment.
          </div>
        </div>
        {loading && (
          <span className="inline-flex items-center gap-1.5 text-xs text-slate-500 animate-pulse">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading…
          </span>
        )}
      </div>

      {err && <div className="text-sm text-red-600" data-testid="fvg-error">{err}</div>}

      {!err && data && !data.has_snapshot && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5"
             data-testid="fvg-no-snapshot">
          No frozen snapshot exists yet for {month}. The nightly warmer will populate one on the next run; until then only the live column is meaningful.
        </div>
      )}

      {!err && data && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="fvg-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-wide text-slate-500 border-b border-emerald-200/60">
                <th className="text-left py-1.5 pr-3">Metric</th>
                <th className="text-right py-1.5 px-2">Frozen {data.frozen_at ? `(${new Date(data.frozen_at).toLocaleDateString("en-IN")})` : ""}</th>
                <th className="text-right py-1.5 px-2">Live (now)</th>
                <th className="text-right py-1.5 pl-2">Growth</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const fv = (data.frozen || {})[r.key] || 0;
                const lv = (data.live || {})[r.key] || 0;
                const gv = data.has_snapshot ? (data.growth || {})[r.key] || 0 : null;
                const fmtVal = r.money ? fmtMoney : fmt;
                const subFrozen = r.subKey ? (data.frozen || {})[r.subKey] : null;
                const subLive = r.subKey ? (data.live || {})[r.subKey] : null;
                return (
                  <tr key={r.key} className="border-t border-emerald-100/60"
                      data-testid={`fvg-row-${r.key}`}>
                    <td className="py-2 pr-3 text-slate-700">{r.label}</td>
                    <td className="py-2 px-2 text-right tabular-nums text-slate-600">
                      {data.has_snapshot ? fmtVal(fv) : "—"}
                      {r.subFmt && subFrozen != null && data.has_snapshot && (
                        <div className="text-[10px] text-slate-400">{r.subFmt(subFrozen)}</div>
                      )}
                    </td>
                    <td className="py-2 px-2 text-right tabular-nums font-medium text-slate-900">
                      {fmtVal(lv)}
                      {r.subFmt && subLive != null && (
                        <div className="text-[10px] text-slate-400">{r.subFmt(subLive)}</div>
                      )}
                    </td>
                    <td className={`py-2 pl-2 text-right tabular-nums font-semibold ${
                      gv == null ? "text-slate-400" :
                      gv > 0 ? "text-emerald-700" :
                      gv < 0 ? "text-red-700" : "text-slate-500"}`}>
                      {gv == null ? "—" : `${gv > 0 ? "+" : ""}${fmtVal(gv)}`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const fmt = (n) => (n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const fmtMoney = (n) => fmt(n);  // Currency-neutral (mixed-currency brands supported per brand)

/** Multi-select popover for filtering the dashboard by one or more campaigns.
 * Scoped to the campaigns owned by the currently-selected team (or all
 * campaigns when team === "all"). Displays name + report_type badge so
 * ops can tell RD-Y247 from FTD-Y247 at a glance. */
function CampaignMultiSelect({ campaigns, value, onChange, open, setOpen }) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return campaigns;
    return campaigns.filter((c) => (c.name || "").toLowerCase().includes(q));
  }, [campaigns, query]);

  const selectedSet = useMemo(() => new Set(value), [value]);
  const toggle = (id) => {
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(Array.from(next));
  };
  const selectAllFiltered = () => {
    const merged = new Set(selectedSet);
    filtered.forEach((c) => merged.add(c.id));
    onChange(Array.from(merged));
  };
  const clearAll = () => onChange([]);

  const label = value.length === 0
    ? "All campaigns"
    : value.length === 1
      ? (campaigns.find((c) => c.id === value[0])?.name || "1 campaign")
      : `${value.length} campaigns`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-10 min-w-[200px] justify-between glass-input px-3"
          data-testid="dash-campaign-multiselect-trigger"
          disabled={campaigns.length === 0}
          title={campaigns.length === 0 ? "No campaigns configured for this team" : "Filter by campaign"}
        >
          <span className="truncate text-slate-700">
            <span className="text-[10px] uppercase tracking-wide text-slate-400 mr-1.5">Campaigns</span>
            {label}
          </span>
          <ChevronsUpDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[320px] p-0"
                      data-testid="dash-campaign-multiselect-panel">
        <div className="p-2 border-b border-slate-200">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search campaigns…"
            className="w-full text-sm px-2 py-1.5 border border-slate-200 rounded outline-none focus:border-slate-400"
            data-testid="dash-campaign-search"
          />
          <div className="flex items-center justify-between mt-1.5 text-[11px]">
            <button
              type="button"
              onClick={selectAllFiltered}
              disabled={filtered.length === 0}
              className="text-slate-600 hover:text-slate-900 disabled:opacity-40"
              data-testid="dash-campaign-select-all">
              Select {filtered.length === campaigns.length ? "all" : `${filtered.length} filtered`}
            </button>
            <button
              type="button"
              onClick={clearAll}
              disabled={value.length === 0}
              className="text-slate-600 hover:text-slate-900 disabled:opacity-40"
              data-testid="dash-campaign-clear">
              Clear
            </button>
          </div>
        </div>
        <div className="max-h-72 overflow-auto p-1">
          {filtered.length === 0 && (
            <div className="text-xs text-slate-500 px-2 py-4 text-center">
              {campaigns.length === 0 ? "No campaigns for this team." : "No campaigns match your search."}
            </div>
          )}
          {filtered.map((c) => {
            const checked = selectedSet.has(c.id);
            return (
              <label
                key={c.id}
                className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm ${checked ? "bg-sky-50" : "hover:bg-slate-50"}`}
                data-testid={`dash-campaign-opt-${c.id}`}
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => toggle(c.id)}
                />
                <span className="flex-1 truncate text-slate-800">{c.name}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${c.report_type === "FTD" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>
                  {c.report_type}
                </span>
              </label>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Compact chip list showing the picked campaigns underneath the filter row.
 * Each chip has an "×" to remove that campaign from the selection without
 * re-opening the popover. */
function CampaignChips({ campaigns, value, onChange }) {
  if (!value.length) return null;
  const byId = new Map(campaigns.map((c) => [c.id, c]));
  return (
    <div className="flex flex-wrap gap-1.5 items-center" data-testid="dash-campaign-chips">
      <span className="text-[10px] uppercase tracking-wide text-slate-400">Filtered by</span>
      {value.map((id) => {
        const c = byId.get(id);
        return (
          <span key={id}
                className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-[11px] text-slate-700"
                data-testid={`dash-campaign-chip-${id}`}>
            {c?.name || id.slice(0, 8)}
            <button type="button"
                    onClick={() => onChange(value.filter((v) => v !== id))}
                    className="rounded-full hover:bg-slate-200 p-0.5"
                    aria-label={`Remove ${c?.name || id}`}
                    data-testid={`dash-campaign-chip-remove-${id}`}>
              <X className="w-2.5 h-2.5" />
            </button>
          </span>
        );
      })}
      <button type="button"
              onClick={() => onChange([])}
              className="text-[11px] text-slate-500 hover:text-slate-900 ml-1"
              data-testid="dash-campaign-chip-clear-all">
        clear all
      </button>
    </div>
  );
}

export default function DashboardPage() {
  const [teams, setTeams] = useState([]);
  const [brandsMap, setBrandsMap] = useState({});
  const [teamId, setTeamId] = useState("all");
  // Multi-select campaigns scoped to the currently-selected team so ops
  // can drill into specific campaigns (e.g. "F7 RD + RD_H + BD-Y247-Neu"
  // for Team S). Empty array = no campaign filter applied.
  const [allCampaigns, setAllCampaigns] = useState([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [campaignsOpen, setCampaignsOpen] = useState(false);
  // Default to the current month so KPIs load with a sensible scope rather
  // than "all-time" (which is slow on large datasets and confusing).
  const _currentMonth = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  })();
  const [dateSel, setDateSel] = useState({ mode: "month", month: _currentMonth, from: "", to: "" });
  const [months, setMonths] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [mom, setMom] = useState([]);
  const [lb, setLb] = useState([]);
  const [brands, setBrands] = useState([]);
  const [funnel, setFunnel] = useState({ stages: [] });
  const [cohort, setCohort] = useState({ months: [], matrix: [] });
  const [aiText, setAiText] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [pivot, setPivot] = useState({ rows: [], grand_total: null, group_by: "brand_id" });
  const [pivotGroupBy, setPivotGroupBy] = useState("brand_id");
  // Independent loading flag for the pivot table so a slow team-grouped
  // aggregate shows an inline spinner instead of appearing frozen (the
  // page-level `loading` waits for all 8 endpoints to finish, so the
  // pivot could be done while others were still running or vice-versa).
  const [pivotLoading, setPivotLoading] = useState(false);
  const [financeBreakdown, setFinanceBreakdown] = useState([]);
  const [retention, setRetention] = useState({ cohorts: [], months_forward: 3 });
  const [retBusy, setRetBusy] = useState(false);
  const [retError, setRetError] = useState("");
  const [retLoaded, setRetLoaded] = useState(false);
  const [retNotice, setRetNotice] = useState("");
  // Global "widgets are fetching" flag so KPI cards can show a shimmer
  // instead of stale 0's when the date filter changes.
  const [loading, setLoading] = useState(false);
  // View lens: "attribution" (default — money linked to conversions this
  // window) or "cashflow" (raw MySQL deposits landed in the window).
  const [viewLens, setViewLens] = useState("attribution");
  const [cashflow, setCashflow] = useState(null);
  const [cashflowLoading, setCashflowLoading] = useState(false);
  // Ref used to invalidate stale fetch responses (race-condition guard).
  const dashboardFetchIdRef = useRef(0);

  // Sortable-table wiring for the three dashboard tables. Each row is
  // fed through useSortableTable so column headers become clickable and
  // headers stay sticky when the table body scrolls.
  const { rows: financeSorted, thProps: fThProps } = useSortableTable(financeBreakdown || [], {
    initialKey: "users_converted", initialDir: "desc",
    accessors: { bucket_label: (r) => (r.bucket_label || "").toLowerCase() },
  });
  const { rows: pivotSorted, thProps: pThProps } = useSortableTable(pivot?.rows || [], {
    initialKey: "users_converted", initialDir: "desc",
    accessors: { group_label: (r) => (r.group_label || "").toLowerCase() },
  });

  useEffect(() => {
    api.get("/teams").then((r) => setTeams(r.data)).catch(() => setTeams([]));
    // Campaigns list drives the multi-select. Uses the admin endpoint
    // when available (returns team_id per campaign so we can filter
    // client-side by the current team dropdown).
    api.get("/admin/campaigns")
      .then((r) => setAllCampaigns(Array.isArray(r.data) ? r.data : []))
      .catch(() => setAllCampaigns([]));
    api.get("/brands").then((r) => {
      const m = {};
      r.data.forEach((b) => { m[b.brand_id] = b; });
      setBrandsMap(m);
    }).catch(() => setBrandsMap({}));
    api.get("/analytics/mom").then((r) => {
      setMonths([...new Set(r.data.map((x) => x.month))].sort());
    });
  }, []);

  // Build the date-scoped param bag once per render.
  const dateParams = useMemo(() => dateParamsFrom(dateSel), [dateSel]);

  // Campaigns filtered to the currently-selected team. "All teams" =>
  // show every campaign; otherwise scope to campaigns owned by that team.
  const teamCampaigns = useMemo(() => {
    if (teamId === "all") return allCampaigns;
    return (allCampaigns || []).filter((c) => c.team_id === teamId);
  }, [allCampaigns, teamId]);

  // Reset the multi-select whenever the team changes — a campaign
  // scoped to Team S makes no sense while filtered to Team N.
  useEffect(() => {
    setSelectedCampaigns([]);
  }, [teamId]);

  // Wire the picked campaigns into the KPI param bag (backend accepts
  // a comma-separated list). Empty array = unset (no filter).
  const analyticsParams = useMemo(() => {
    const p = { ...dateParams };
    if (teamId && teamId !== "all") p.team_id = teamId;
    if (selectedCampaigns.length > 0) p.campaign_ids = selectedCampaigns.join(",");
    return p;
  }, [dateParams, teamId, selectedCampaigns]);

  useEffect(() => {
    if (viewLens !== "cashflow") return;
    if (!dateParams.date_from || !dateParams.date_to) return;
    setCashflowLoading(true);
    api.get("/ops/analytics/cashflow", { params: dateParams })
      .then((r) => setCashflow(r.data))
      .catch(() => setCashflow(null))
      .finally(() => setCashflowLoading(false));
  }, [viewLens, dateParams]);

  // Params for daily-summary widget (team + date scope combined)
  const dailyParams = useMemo(() => {
    const p = { ...dateParams };
    if (teamId !== "all") p.team_id = teamId;
    return p;
  }, [dateParams, teamId]);

  useEffect(() => {
    const params = { ...dateParams };
    if (teamId !== "all") params.team_id = teamId;
    // Campaign multi-select — only affects endpoints where the backend
    // supports it (currently: /analytics/kpis via _kpis_from_raw). Others
    // ignore the extra param harmlessly.
    if (selectedCampaigns.length > 0) params.campaign_ids = selectedCampaigns.join(",");
    setLoading(true);
    // Cancel any in-flight fetches from a previous filter so late
    // responses can't overwrite the current state. Also stamp a fetch id
    // so if the AbortController misses (already-decoded payload), the
    // .then() handlers drop late data.
    const controller = new AbortController();
    const fetchId = Date.now();
    dashboardFetchIdRef.current = fetchId;
    const cfg = { signal: controller.signal };
    setPivotLoading(true);
    const guard = (setter, expectArray = false) => (r) => {
      if (dashboardFetchIdRef.current !== fetchId) return;
      // Defensive: some cached endpoints may return a wrapped dict
      // for what used to be an array. Coerce back to a safe shape so
      // downstream .map() calls can't blow up the whole dashboard.
      let d = r.data;
      if (expectArray && !Array.isArray(d)) d = [];
      setter(d);
    };
    Promise.all([
      api.get("/analytics/kpis", { params, ...cfg }).then(guard(setKpis)).catch(() => {}),
      api.get("/analytics/mom", { params: { team_id: params.team_id }, ...cfg }).then(guard(setMom, true)).catch(() => {}),
      api.get("/analytics/leaderboard", { params, ...cfg }).then(guard(setLb, true)).catch(() => {}),
      api.get("/analytics/brands", { params, ...cfg }).then(guard(setBrands, true)).catch(() => {}),
      api.get("/analytics/funnel", { params, ...cfg }).then(guard(setFunnel)).catch(() => {}),
      api.get("/analytics/cohort", { params: { team_id: params.team_id }, ...cfg }).then(guard(setCohort)).catch(() => {}),
      api.get("/analytics/pivot", { params: { ...params, group_by: pivotGroupBy }, ...cfg })
        .then(guard(setPivot))
        .catch(() => {})
        .finally(() => {
          if (dashboardFetchIdRef.current === fetchId) setPivotLoading(false);
        }),
      api.get("/analytics/finance-breakdown", { params, ...cfg }).then(guard(setFinanceBreakdown, true)).catch(() => {}),
    ]).finally(() => {
      // Only clear loading if this was the LATEST batch; otherwise a
      // stale batch finishing after would flip us out of loading state.
      if (dashboardFetchIdRef.current === fetchId) setLoading(false);
    });
    setAiText("");
    return () => controller.abort();
  }, [teamId, dateParams, pivotGroupBy, selectedCampaigns]);

  const generateAi = async () => {
    setAiBusy(true);
    try {
      const params = { ...dateParams };
      if (teamId !== "all") params.team_id = teamId;
      const r = await api.post("/analytics/ai-insights", null, { params });
      setAiText(r.data.insight_markdown || "");
    } finally {
      setAiBusy(false);
    }
  };

  const loadRetention = async () => {
    setRetBusy(true); setRetError(""); setRetNotice("");
    try {
      const params = { months_forward: 3 };
      if (teamId !== "all") params.team_id = teamId;
      const r = await api.get("/analytics/self-retention", { params });
      if (r.data?.error) {
        setRetError(r.data.error);
        setRetention({ cohorts: [], months_forward: 3 });
      } else {
        setRetention({
          cohorts: r.data?.cohorts || [],
          months_forward: r.data?.months_forward || 3,
        });
        if (r.data?.notice) setRetNotice(r.data.notice);
        else if (!(r.data?.cohorts?.length)) {
          setRetNotice("Query completed — no cohort data returned for this scope.");
        }
      }
      setRetLoaded(true);
    } catch (ex) {
      setRetError(ex?.response?.data?.detail || ex?.message || "Failed to load retention data.");
      setRetLoaded(true);
    } finally {
      setRetBusy(false);
    }
  };

  // Frozen-vs-Growth is replaced by MySQL-live deposits split — see
  // DepositsSplitPanel below. We keep the frozen-vs-growth month calc
  // in case ops still opens the underlying route.
  const funnelData = useMemo(() => funnel.stages || [], [funnel]);

  // The frozen-vs-growth card is only meaningful for a single closed
  // month (previous months relative to today). Derive it from the
  // current date selector rather than a separate control.
  const frozenMonth = useMemo(() => {
    if (dateSel.mode !== "month" || !dateSel.month) return null;
    const now = new Date();
    const [y, m] = dateSel.month.split("-").map(Number);
    if (!y || !m) return null;
    const firstOfNext = new Date(y, m, 1);   // month index m => 0-indexed next month
    // Give a 1-day buffer to match ANALYTICS_SNAPSHOT_BUFFER_DAYS default.
    return now.getTime() > firstOfNext.getTime() + 24 * 3600 * 1000
      ? dateSel.month
      : null;
  }, [dateSel]);

  return (
    <div className="space-y-6">
      <MainFreshnessBanner />
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-outfit font-semibold text-3xl md:text-4xl text-slate-900 tracking-tight">Dashboard</h1>
          <p className="text-sm text-slate-600 mt-1">Reactivation performance across teams, brands, and months.</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <Select value={teamId} onValueChange={setTeamId}>
            <SelectTrigger className="w-[180px] glass-input" data-testid={REPORTS.filterTeam}>
              <SelectValue placeholder="Team" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All teams</SelectItem>
              {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <CampaignMultiSelect
            campaigns={teamCampaigns}
            value={selectedCampaigns}
            onChange={setSelectedCampaigns}
            open={campaignsOpen}
            setOpen={setCampaignsOpen}
          />
          <DateRangeControl value={dateSel} onChange={setDateSel} months={months} testIdPrefix="dash" />
        </div>
      </div>

      {selectedCampaigns.length > 0 && (
        <CampaignChips
          campaigns={teamCampaigns}
          value={selectedCampaigns}
          onChange={setSelectedCampaigns}
        />
      )}

      {/* KPIs — meaningful for a list-of-reactivated-users backfill */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          {viewLens === "attribution"
            ? "Attribution lens — money linked to users converted in this window (incl. their later lifetime deposits)."
            : "Cashflow lens — deposits that ACTUALLY landed in MySQL during this window, regardless of credit month."}
          <CachedBadge at={kpis?._cached_at} />
        </div>
        <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5 text-[11px]"
             data-testid="lens-toggle">
          <button type="button" onClick={() => setViewLens("attribution")}
            className={`px-2.5 py-1 rounded ${viewLens === "attribution" ? "bg-white shadow-sm border border-slate-200 text-slate-900 font-medium" : "text-slate-500 hover:text-slate-700"}`}
            data-testid="lens-attribution"
            title="Money earned FROM users we converted this month, incl. their subsequent lifetime deposits.">
            Attribution
          </button>
          <button type="button" onClick={() => setViewLens("cashflow")}
            className={`px-2.5 py-1 rounded ${viewLens === "cashflow" ? "bg-white shadow-sm border border-slate-200 text-slate-900 font-medium" : "text-slate-500 hover:text-slate-700"}`}
            data-testid="lens-cashflow"
            title="Money that ACTUALLY hit MySQL during the selected window (raw payment.payment_txns). Includes non-attributed deposits too.">
            Cashflow
          </button>
        </div>
      </div>
      <LoadingOverlay loading={loading} testId="dashboard-kpis-loading">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <Kpi label="Reactivated users" value={fmt(kpis?.reactivated)} icon={Users} sub={`of ${kpis?.total_users || 0} in file`} />
          <Kpi label="FTD on-day"
               value={fmtMoney(kpis?.ftd_on_day)}
               icon={Sparkles}
               sub={`${fmt(kpis?.ftd_on_day_count)} first-time depositors`} />
          <Kpi label="Deposits on-day (total)" value={fmtMoney(kpis?.sum_on)} icon={Target}
               sub={`incl. top-ups · ${kpis?.deposits_on || 0} txns`} />
          <Kpi label="Deposits after date" value={fmtMoney(kpis?.sum_after)} icon={TrendingUp} sub={`${kpis?.deposits_after || 0} txns`} />
          <Kpi label="Approx LTV" value={fmtMoney(kpis?.ltv)} icon={PiggyBank} sub="Before + On + After" />
        </div>
      </LoadingOverlay>

      {/* MySQL-live deposits split — only meaningful for a single-month scope. */}
      {dateSel.mode === "month" && dateSel.month && (
        <DepositsSplitPanel
          month={dateSel.month}
          teamId={teamId}
        />
      )}

      {viewLens === "cashflow" && (
        <div className="glass-card p-4 border-l-4 border-sky-400 bg-sky-50/40"
             data-testid="cashflow-panel">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-outfit font-semibold text-slate-900">Cashflow view</div>
              <div className="text-xs text-slate-500">Raw MySQL <span className="font-mono">status=PROCESSED</span> deposits in the selected date range. Independent of attribution.</div>
            </div>
            {cashflowLoading && (
              <span className="inline-flex items-center gap-1.5 text-xs text-slate-500 animate-pulse">
                <span className="inline-block w-3 h-3 border-2 border-slate-300 border-t-slate-700 rounded-full animate-spin" />
                Loading…
              </span>
            )}
          </div>
          {cashflow?.grand_total ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <MiniKpi label="FTD amount" value={fmtMoney(cashflow.grand_total.ftd_amount)}
                         sub={`${fmt(cashflow.grand_total.ftd_users)} first-time depositors`} />
                <MiniKpi label="Total deposits" value={fmtMoney(cashflow.grand_total.total_amount)}
                         sub={`${fmt(cashflow.grand_total.total_txns)} txns`} />
                <MiniKpi label="Unique depositors" value={fmt(cashflow.grand_total.unique_users)}
                         sub={`${cashflow.brands_included?.length || 0} brands`} />
                <MiniKpi label="Repeat share"
                         value={cashflow.grand_total.unique_users > 0
                            ? `${Math.round(100 * (1 - (cashflow.grand_total.ftd_users / cashflow.grand_total.unique_users)))}%`
                            : "—"}
                         sub="non-FTD depositors" />
              </div>
              {cashflow.per_brand?.length > 1 && (
                <div className="mt-3 overflow-auto max-h-56 border border-sky-100 rounded">
                  <table className="text-[11px] w-full">
                    <thead className="bg-sky-50 sticky top-0 text-left">
                      <tr>
                        <th className="px-2 py-1">Brand</th>
                        <th className="px-2 py-1 text-right">Total txns</th>
                        <th className="px-2 py-1 text-right">Total amount</th>
                        <th className="px-2 py-1 text-right">Unique</th>
                        <th className="px-2 py-1 text-right">FTD users</th>
                        <th className="px-2 py-1 text-right">FTD amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cashflow.per_brand.map((b) => (
                        <tr key={b.brand_id} className="border-t border-sky-50">
                          <td className="px-2 py-1 font-medium">{brandsMap[b.brand_id]?.brand_name || `#${b.brand_id}`}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{fmt(b.total_txns)}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{fmtMoney(b.total_amount)}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{fmt(b.unique_users)}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{fmt(b.ftd_users)}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{fmtMoney(b.ftd_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="text-[10px] text-slate-500 mt-2">
                Note: cashflow numbers are mixed-currency when spanning brands with different currencies. Filter to one brand for a currency-clean total.
              </div>
            </>
          ) : (!cashflowLoading && (
            <div className="text-xs text-slate-500">Pick a date range to load the cashflow view.</div>
          ))}
        </div>
      )}

      {/* Frozen-snapshot vs live growth — only when a single closed month is picked. */}
      {frozenMonth && (
        <FrozenVsGrowthCard
          month={frozenMonth}
          teamId={teamId}
          campaignIds={selectedCampaigns}
        />
      )}

      {/* Daily summary — unified across backfill + ops, with agent drilldown */}
      <DailySummaryCard
        endpoint="/analytics/daily-summary"
        agentEndpoint="/ops/analytics/agents"
        params={dailyParams}
        title="Daily summary"
        subtitle="Per-day rollup blending historical backfill + live ops uploads. Click a row to see per-agent breakdown."
      />

      {/* Same rollup but split by team — helps ops leads compare team performance */}
      <DailySummaryByTeamCard
        params={dailyParams}
        title="Daily summary by team"
        subtitle="Same metrics as above, one row per (date × team) — sourced from live ops uploads only."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* MoM trend — deposit money only (rates would be tautological on filtered data) */}
        <div className="glass-card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <div className="font-outfit font-semibold text-slate-900">Month over month</div>
            <div className="text-xs text-slate-500">Reactivators + deposit money</div>
          </div>
          <div className="h-72">
            <ResponsiveContainer>
              <LineChart data={mom} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.06)" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                <Tooltip />
                <Line yAxisId="left" type="monotone" dataKey="reactivated" name="Reactivators" stroke="#0A84FF" strokeWidth={2.5} dot={{ r: 3 }} />
                <Line yAxisId="right" type="monotone" dataKey="sum_after" name="Deposits after" stroke="#5B8C5A" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Post-reactivation engagement (not a "funnel" — everyone in the dataset already reactivated) */}
        <div className="glass-card p-5">
          <div className="font-outfit font-semibold text-slate-900 mb-1">Post-reactivation engagement</div>
          <div className="text-xs text-slate-500 mb-3">Depth of engagement among confirmed reactivators.</div>
          <div className="h-72">
            <ResponsiveContainer>
              <BarChart data={funnelData} layout="vertical" margin={{ left: 20 }}>
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="value" fill="#0A84FF" radius={[6, 6, 6, 6]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Leaderboard */}
        <div className="glass-card p-5">
          <div className="font-outfit font-semibold text-slate-900 mb-3">Team leaderboard</div>
          <div className="space-y-2 max-h-72 overflow-auto pr-1">
            {lb.length === 0 && <div className="text-sm text-slate-500">No data yet.</div>}
            {lb.map((t, idx) => (
              <div key={t.team_id} className="glass-solid p-3 flex items-center gap-3">
                <div className="w-7 h-7 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-semibold">
                  {idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-900 font-medium truncate">{t.team_name}</div>
                  <div className="text-xs text-slate-500">{t.reactivated} reactivators · {t.total_users} rows</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold text-slate-900">{fmtMoney(t.sum_after)}</div>
                  <div className="text-[11px] text-slate-500">deposits after</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Brand breakdown — donut with side legend so tiny slices don't collide */}
        <div className="glass-card p-5">
          <div className="font-outfit font-semibold text-slate-900 mb-3">Brand breakdown (deposits after)</div>
          {(() => {
            const rows = brands
              .map((b) => ({
                brand_id: b.brand_id,
                label: brandsMap[b.brand_id]?.brand_name || `#${b.brand_id}`,
                currency: brandsMap[b.brand_id]?.currency || "",
                value: b.sum_after || 0,
              }))
              .filter((r) => r.value > 0)
              .sort((a, b) => b.value - a.value);
            const total = rows.reduce((s, r) => s + r.value, 0);
            return (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                <div className="h-64 relative">
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={rows}
                        dataKey="value"
                        nameKey="label"
                        innerRadius={60}
                        outerRadius={95}
                        stroke="#fff"
                        strokeWidth={2}
                        paddingAngle={1}
                      >
                        {rows.map((_, i) => (
                          <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(v, _n, entry) => [
                          `${fmt(v)} ${entry.payload.currency} · ${
                            total ? ((v / total) * 100).toFixed(1) : 0
                          }%`,
                          entry.payload.label,
                        ]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  {/* Total in the middle of the donut */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <div className="text-[10px] uppercase tracking-wide text-slate-500">Total</div>
                    <div className="font-outfit font-semibold text-slate-900 text-lg leading-none mt-0.5">
                      {fmt(total)}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{rows[0]?.currency || ""}</div>
                  </div>
                </div>
                <div className="max-h-64 overflow-y-auto pr-1" data-testid="brand-legend">
                  {rows.length === 0 && (
                    <div className="text-xs text-slate-500 italic">No brand-scoped deposits in range.</div>
                  )}
                  {rows.map((r, i) => {
                    const pct = total ? (r.value / total) * 100 : 0;
                    return (
                      <div key={r.brand_id} className="flex items-center gap-2 py-1.5 text-xs">
                        <span
                          className="w-2.5 h-2.5 rounded-sm shrink-0"
                          style={{ background: CHART_COLORS[i % CHART_COLORS.length] }}
                        />
                        <span className="font-medium text-slate-900 truncate">{r.label}</span>
                        <span className="ml-auto tabular-nums text-slate-600">{fmt(r.value)}</span>
                        <span className="tabular-nums text-slate-400 w-12 text-right">
                          {pct.toFixed(1)}%
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>

        {/* AI insights */}
        <div className="glass-card p-5 lg:col-span-1">
          <div className="flex items-center justify-between mb-3">
            <div className="font-outfit font-semibold text-slate-900 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#0A84FF]" /> AI insights — cross-report
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={generateAi}
              disabled={aiBusy}
              className="btn-tactile"
              data-testid={REPORTS.aiGenerateBtn}
            >
              {aiBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Generate"}
            </Button>
          </div>
          <div className="text-sm text-slate-700 max-h-72 overflow-auto prose prose-sm">
            {aiText ? <ReactMarkdown>{aiText}</ReactMarkdown> : (
              <p className="text-slate-500">
                Click <b>Generate</b> for a Claude-authored narrative that unifies <b>KPIs · MoM · team leaderboard · brand mix · retention D30/D60/D90 · funnel · TTC · top dispositions</b> into one analysis for the selected date range.
              </p>
            )}
          </div>
        </div>
      </div>


      {/* Finance breakdown by credit channel — voice / SMS / WN / Invalid / Unsure.
          Each converted user counted exactly once, bucketed by their highest-priority
          credit in the window. Helps see "how much of the reactivated ₹X came from
          each channel". */}
      {financeBreakdown && financeBreakdown.length > 0 && (
        <div className="glass-card p-4 overflow-x-auto" data-testid="finance-breakdown-card">
          <div className="mb-3">
            <div className="font-outfit font-semibold text-slate-900">Converted users — finance split by credit channel</div>
            <div className="text-xs text-slate-500 mt-0.5">
              Each converted user counted once, bucketed by their highest-priority credit
              (Voice &gt; SMS &gt; WN &gt; Invalid &gt; Unsure). Sums to the pivot&apos;s totals.
            </div>
          </div>
          <div className="overflow-auto max-h-[60vh]">
            <table className="w-full text-sm">
              <thead data-sticky>
                <tr className="text-left text-slate-500 border-b border-slate-200/60">
                  <SortableTh {...fThProps("bucket_label", "left")}>Channel</SortableTh>
                  <SortableTh {...fThProps("users_converted")}>Users converted</SortableTh>
                  <SortableTh {...fThProps("amount_on_day")}>Amount on day</SortableTh>
                  <SortableTh {...fThProps("txns_on_day")}>Txns on day</SortableTh>
                  <SortableTh {...fThProps("amount_after")}>Amount after</SortableTh>
                  <SortableTh {...fThProps("txns_after")}>Txns after</SortableTh>
                </tr>
              </thead>
              <tbody>
                {financeSorted.map((r) => (
                <tr key={r.bucket} className="border-t border-white/60" data-testid={`finance-row-${r.bucket}`}>
                  <td className="py-1.5 px-2">
                    <span className={
                      r.bucket === "yes" ? "text-emerald-700 font-medium" :
                      r.bucket === "sms" ? "text-blue-700 font-medium" :
                      r.bucket === "wn" ? "text-purple-700 font-medium" :
                      r.bucket === "invalid" ? "text-red-700 font-medium" :
                      "text-amber-700 font-medium"
                    }>{r.bucket_label}</span>
                  </td>
                  <td className="py-1.5 px-2 text-right font-mono">{r.users_converted.toLocaleString()}</td>
                  <td className="py-1.5 px-2 text-right font-mono">{r.amount_on_day.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                  <td className="py-1.5 px-2 text-right font-mono text-slate-500">{r.txns_on_day.toLocaleString()}</td>
                  <td className="py-1.5 px-2 text-right font-mono">{r.amount_after.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                  <td className="py-1.5 px-2 text-right font-mono text-slate-500">{r.txns_after.toLocaleString()}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-slate-300 bg-slate-50/40 font-semibold">
                <td className="py-1.5 px-2">Total</td>
                <td className="py-1.5 px-2 text-right font-mono">
                  {financeBreakdown.reduce((s, r) => s + r.users_converted, 0).toLocaleString()}
                </td>
                <td className="py-1.5 px-2 text-right font-mono">
                  {financeBreakdown.reduce((s, r) => s + r.amount_on_day, 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </td>
                <td className="py-1.5 px-2 text-right font-mono">
                  {financeBreakdown.reduce((s, r) => s + r.txns_on_day, 0).toLocaleString()}
                </td>
                <td className="py-1.5 px-2 text-right font-mono">
                  {financeBreakdown.reduce((s, r) => s + r.amount_after, 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </td>
                <td className="py-1.5 px-2 text-right font-mono">
                  {financeBreakdown.reduce((s, r) => s + r.txns_after, 0).toLocaleString()}
                </td>
              </tr>
            </tbody>
          </table>
          </div>
        </div>
      )}


      {/* Pivot summary — matches user's Excel PowerPivot */}
      <div className="glass-card p-4 overflow-x-auto">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <div>
            <div className="font-outfit font-semibold text-slate-900">Conversion summary (pivot)</div>
            <div className="text-xs text-slate-500 mt-0.5">Only converted users (deposited on/after call date). Money columns are mixed-currency when grouped across brands.</div>
          </div>
          <div className="flex gap-2 items-center">
            <span className="text-xs text-slate-500">Group by</span>
            <div className="glass-solid p-0.5 rounded-lg flex text-xs">
              <button onClick={() => setPivotGroupBy("brand_id")}
                disabled={pivotLoading}
                className={`px-3 py-1 rounded-md transition-colors ${pivotGroupBy === "brand_id" ? "bg-white shadow font-medium text-slate-900" : "text-slate-500"} ${pivotLoading ? "opacity-60 cursor-wait" : ""}`}
                data-testid="pivot-groupby-brand">Brand</button>
              <button onClick={() => setPivotGroupBy("team_id")}
                disabled={pivotLoading}
                className={`px-3 py-1 rounded-md transition-colors ${pivotGroupBy === "team_id" ? "bg-white shadow font-medium text-slate-900" : "text-slate-500"} ${pivotLoading ? "opacity-60 cursor-wait" : ""}`}
                data-testid="pivot-groupby-team">Team</button>
            </div>
            {pivotLoading && (
              <span className="inline-flex items-center gap-1.5 text-xs text-slate-500 animate-pulse ml-1"
                    data-testid="pivot-loading">
                <span className="inline-block w-3 h-3 border-2 border-slate-300 border-t-slate-700 rounded-full animate-spin" />
                Loading…
              </span>
            )}
          </div>
        </div>
        <div className={`overflow-auto max-h-[65vh] transition-opacity ${pivotLoading ? "opacity-50 pointer-events-none" : ""}`}>
        <table className="w-full text-xs">
          <thead data-sticky>
            <tr className="text-slate-500 border-b border-white/60">
              <SortableTh {...pThProps("group_label", "left")} className="sticky left-0 bg-white/60 backdrop-blur">{pivotGroupBy === "brand_id" ? "Brand" : "Team"}</SortableTh>
              <SortableTh {...pThProps("users_converted")}>Users converted</SortableTh>
              <SortableTh {...pThProps("deposits_before_conversion")}>Deposits before</SortableTh>
              <SortableTh {...pThProps("amount_before_conversion")}>Amount before</SortableTh>
              <SortableTh {...pThProps("avg_days_since_last_deposit")}>Avg days since last dep</SortableTh>
              <SortableTh {...pThProps("total_ftd_on_day")}>Total FTD on day</SortableTh>
              <SortableTh {...pThProps("total_amount_on_day")}>Amount on day</SortableTh>
              <SortableTh {...pThProps("txns_on_day")}>Txns on day</SortableTh>
              <SortableTh {...pThProps("txns_after")}>Txns after</SortableTh>
              <SortableTh {...pThProps("amount_after")}>Amount after</SortableTh>
            </tr>
          </thead>
          <tbody>
            {pivotSorted.map((r) => (
              <tr key={String(r.group_key)} className="border-t border-white/40 hover:bg-white/40">
                <td className="py-2 px-2 font-medium sticky left-0 bg-white/60 backdrop-blur">
                  {r.group_label} {r.currency && <span className="text-[10px] text-slate-400 ml-1">({r.currency})</span>}
                </td>
                <td className="py-2 px-2 text-right">{fmt(r.users_converted)}</td>
                <td className="py-2 px-2 text-right">{fmt(r.deposits_before_conversion)}</td>
                <td className="py-2 px-2 text-right">{fmt(r.amount_before_conversion)}</td>
                <td className="py-2 px-2 text-right">{r.avg_days_since_last_deposit}</td>
                <td className="py-2 px-2 text-right">{fmt(r.total_ftd_on_day)}</td>
                <td className="py-2 px-2 text-right">{fmt(r.total_amount_on_day)}</td>
                <td className="py-2 px-2 text-right">{fmt(r.txns_on_day)}</td>
                <td className="py-2 px-2 text-right">{fmt(r.txns_after)}</td>
                <td className="py-2 px-2 text-right font-semibold text-slate-900">{fmt(r.amount_after)}</td>
              </tr>
            ))}
            {pivot.grand_total && pivot.rows.length > 0 && (
              <tr className="border-t-2 border-slate-900/70 bg-slate-900 text-white">
                <td className="py-2 px-2 font-semibold sticky left-0 bg-slate-900 text-white">{pivot.grand_total.group_label}</td>
                <td className="py-2 px-2 text-right font-semibold">{fmt(pivot.grand_total.users_converted)}</td>
                <td className="py-2 px-2 text-right font-semibold">{fmt(pivot.grand_total.deposits_before_conversion)}</td>
                <td className="py-2 px-2 text-right font-semibold">{fmt(pivot.grand_total.amount_before_conversion)}</td>
                <td className="py-2 px-2 text-right font-semibold">{pivot.grand_total.avg_days_since_last_deposit}</td>
                <td className="py-2 px-2 text-right font-semibold">{fmt(pivot.grand_total.total_ftd_on_day)}</td>
                <td className="py-2 px-2 text-right font-semibold">{fmt(pivot.grand_total.total_amount_on_day)}</td>
                <td className="py-2 px-2 text-right font-semibold">{fmt(pivot.grand_total.txns_on_day)}</td>
                <td className="py-2 px-2 text-right font-semibold">{fmt(pivot.grand_total.txns_after)}</td>
                <td className="py-2 px-2 text-right font-semibold">{fmt(pivot.grand_total.amount_after)}</td>
              </tr>
            )}
            {pivotSorted.length === 0 && (
              <tr><td colSpan={10} className="py-8 text-center text-slate-500">No converted users yet. Upload a backfill or run a daily calling list.</td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* Self-retention (MySQL-backed) */}
      <div className="glass-card p-5 overflow-x-auto">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <div>
            <div className="font-outfit font-semibold text-slate-900">Self-retention after reactivation</div>
            <div className="text-xs text-slate-500 mt-0.5">
              For each cohort, how many kept depositing on their own in later months (self-retained) vs went dormant again and had to be called (called-again). Queries the payment DB.
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={loadRetention} disabled={retBusy} data-testid="retention-load-btn">
            {retBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Load / refresh"}
          </Button>
        </div>
        {retError && <div className="text-sm text-red-600 mb-3" data-testid="retention-error">{retError}</div>}
        {retNotice && !retError && (
          <div className="text-sm text-amber-700 mb-3" data-testid="retention-notice">{retNotice}</div>
        )}
        {retention.cohorts.length === 0 && !retError && !retNotice ? (
          <div className="text-sm text-slate-500" data-testid="retention-placeholder">
            {retLoaded
              ? "Loaded, but nothing to show yet."
              : "Click \"Load / refresh\" — this hits your production MySQL, so it may take a few seconds."}
          </div>
        ) : retention.cohorts.length > 0 ? (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-slate-500 border-b border-white/60">
                <th className="text-left py-2 pr-3">Cohort month</th>
                <th className="text-right py-2 pr-3">Cohort size</th>
                {[1, 2, 3].map((n) => (
                  <th key={n} colSpan={2} className="text-left py-2 pr-3">M+{n}</th>
                ))}
              </tr>
              <tr className="text-slate-400 text-[10px] uppercase">
                <th></th><th></th>
                {[1, 2, 3].map((n) => (
                  <React.Fragment key={n}>
                    <th className="text-right py-1 pr-3">Self-retained</th>
                    <th className="text-right py-1 pr-3">Called again</th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {retention.cohorts.map((c) => (
                <tr key={c.cohort_month} className="border-t border-white/40">
                  <td className="py-2 pr-3 font-medium">{c.cohort_month}</td>
                  <td className="py-2 pr-3 text-right">{fmt(c.cohort_size)}</td>
                  {c.offsets.map((o) => (
                    <React.Fragment key={o.offset}>
                      <td className="py-2 pr-3 text-right">
                        <span className="text-emerald-700 font-medium">{o.self_retained_pct}%</span>{" "}
                        <span className="text-slate-500">({o.self_retained})</span>
                      </td>
                      <td className="py-2 pr-3 text-right">
                        <span className={o.called_again_pct > 30 ? "text-amber-700 font-medium" : "text-slate-600"}>{o.called_again_pct}%</span>{" "}
                        <span className="text-slate-500">({o.called_again})</span>
                      </td>
                    </React.Fragment>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </div>

      {/* Cohort */}
      <div className="glass-card p-5 overflow-x-auto">
        <div className="font-outfit font-semibold text-slate-900 mb-3">Retention cohort (reactivated users)</div>
        {cohort.matrix.length === 0 ? (
          <div className="text-sm text-slate-500">Not enough data yet.</div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-slate-500">
                <th className="text-left py-2 pr-3">Cohort</th>
                <th className="text-left py-2 pr-3">Size</th>
                {Array.from({ length: 6 }).map((_, i) => (
                  <th key={i} className="text-left py-2 pr-3">M+{i}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cohort.matrix.map((row) => (
                <tr key={row.cohort} className="border-t border-white/60">
                  <td className="py-2 pr-3 font-medium text-slate-800">{row.cohort}</td>
                  <td className="py-2 pr-3">{row.size}</td>
                  {Array.from({ length: 6 }).map((_, i) => {
                    const key = `+${i}`;
                    const c = row.offsets[key];
                    if (!c) return <td key={i} className="py-2 pr-3 text-slate-400">—</td>;
                    const intensity = Math.max(6, Math.round(c.pct));
                    return (
                      <td key={i} className="py-2 pr-3">
                        <div
                          className="rounded-md px-2 py-1 inline-block text-slate-900"
                          style={{ background: `rgba(10,132,255,${(intensity / 100) * 0.35 + 0.05})` }}
                        >
                          {c.pct.toFixed(0)}% <span className="text-slate-500">({c.count})</span>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
